var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React, { useRef, useMemo, useCallback, useEffect, } from 'react';
import throttle from 'lodash.throttle';
import { SliderWrapper, SliderInnerWrapper, TrackWrapper, StyledTrack, ScrollButtonWrapper, ScrollControl, ScrollControlRight, } from './styles';
import { ProgressIndicator, SliderCard, SliderItem } from './Components';
import { considerBoundaries, fakeScroll, getClientRects, cycleSlice } from './Functions';
import useSliderState from './useSliderState';
import getStyleTypeConfig from './getSliderConfig';
import CustomLink from './Components/CustomLink';
/**
 * 'Raw' Slider component that allows many slider settings.
 * !!For a Slider with preconfigured settings (eg. "default" or "type1") choose the default export of this file!!
 * @param props All slider settings. More information under "./README.md"
 */
var BasicSlider = function (props) {
    var _a;
    // Setting sliderWidth by SliderRef to be able to use Slider component in a smaller wrapper, too.
    var sliderWrapperRef = React.useRef(null);
    var trackWrapperRef = useRef(null);
    var trackRef = useRef(null);
    var fakeScrollInterval = useRef(0);
    var _b = props, children = _b.children, _c = _b.className, className = _c === void 0 ? 'emma-slider' : _c;
    var sliderItemsCount = React.Children.count(children);
    // Get all values from useSliderState
    var _d = useSliderState((_a = sliderWrapperRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth, trackRef, trackWrapperRef, props), arrows = _d.arrows, arrowsDistance = _d.arrowsDistance, arrowsOnHover = _d.arrowsOnHover, background = _d.background, clickProgressDot = _d.clickProgressDot, currentScrollDistance = _d.currentScrollDistance, customLinkProps = _d.customLinkProps, dots = _d.dots, dotsColor = _d.dotsColor, dotsColorInactive = _d.dotsColorInactive, dotsDistance = _d.dotsDistance, dotsSize = _d.dotsSize, dragActive = _d.dragActive, extraItemsForInfinite = _d.extraItemsForInfinite, finishTrackMoving = _d.finishTrackMoving, framePadding = _d.framePadding, frameSmallerThanTrack = _d.frameSmallerThanTrack, hasCardLayout = _d.hasCardLayout, headline = _d.headline, infinite = _d.infinite, isHovering = _d.isHovering, itemOverflow = _d.itemOverflow, lazyLoad = _d.lazyLoad, lazyLoadItemWidth = _d.lazyLoadItemWidth, lazyLoadThreshold = _d.lazyLoadThreshold, maxScrollPosRight = _d.maxScrollPosRight, pauseOnHover = _d.pauseOnHover, posX = _d.posX, progress = _d.progress, progressBarColor = _d.progressBarColor, progressBar = _d.progressBar, progressBarDistance = _d.progressBarDistance, progressBarHeight = _d.progressBarHeight, prevArrow = _d.prevArrow, nextArrow = _d.nextArrow, scroll = _d.scroll, scrollDistance = _d.scrollDistance, scrollOvershootTime = _d.scrollOvershootTime, setDragActive = _d.setDragActive, setIsHovering = _d.setIsHovering, setPosX = _d.setPosX, setTouchActive = _d.setTouchActive, setTouchPosX = _d.setTouchPosX, setTrackIsMoving = _d.setTrackIsMoving, setLoadedSliderItems = _d.setLoadedSliderItems, slideGap = _d.slideGap, sliderInnerWidth = _d.sliderInnerWidth, sliderPaddingBottom = _d.sliderPaddingBottom, sliderWidth = _d.sliderWidth, speed = _d.speed, touchActive = _d.touchActive, touchMove = _d.touchMove, touchPosX = _d.touchPosX, trackIsMoving = _d.trackIsMoving, trackWidth = _d.trackWidth, variableWidth = _d.variableWidth, loadedSliderItems = _d.loadedSliderItems, virtualTrackWidth = _d.virtualTrackWidth;
    /**
     * Fake scrollEvents while moving the slider so lazyLoading content that depends on these events can work
     */
    useEffect(function () {
        clearTimeout(fakeScrollInterval.current);
        if (trackIsMoving) {
            fakeScrollInterval.current = window.setInterval(function () { return fakeScroll(); }, 300);
        }
        return function () { return clearTimeout(fakeScrollInterval.current); };
    }, [trackIsMoving]);
    var handleTouchStart = function (target) { return function (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    event) {
        if (!touchMove || !frameSmallerThanTrack)
            return;
        setTouchActive(target);
        // If touch starts while still overshooting
        if (scrollOvershootTime) {
            finishTrackMoving({ keepTouchActive: target });
        }
    }; };
    /**
     * Sets posX and startPosX via the touch event
     * @param event touch event
     */
    var handleTouchMove = throttle(function (event) {
        if (frameSmallerThanTrack && touchActive) {
            var touchPosXValue_1 = null;
            if (event.touches) {
                touchPosXValue_1 = event.touches[0].clientX;
            }
            else if (dragActive) {
                touchPosXValue_1 = event.clientX;
            }
            if (touchPosXValue_1 !== null) {
                if (!trackIsMoving)
                    setTrackIsMoving(true);
                setTouchPosX(function (oldTouchPosX) {
                    var time = Date.now();
                    var newScrollSpeed = 0;
                    if (oldTouchPosX !== null && time - oldTouchPosX.time < 100) {
                        newScrollSpeed =
                            touchActive === 'progressBar'
                                ? 0
                                : (touchPosXValue_1 - oldTouchPosX.value) / (time - oldTouchPosX.time);
                        var newPosXValue = Math.round(posX.value -
                            (touchActive === 'progressBar'
                                ? -(virtualTrackWidth / (sliderInnerWidth - slideGap))
                                : 1) *
                                (oldTouchPosX.value - (touchPosXValue_1 || 0)));
                        if (newPosXValue !== posX.value) {
                            setPosX({
                                value: touchActive === 'progressBar'
                                    ? considerBoundaries(newPosXValue, maxScrollPosRight)
                                    : newPosXValue,
                                time: time,
                            });
                        }
                    }
                    return {
                        value: touchPosXValue_1,
                        speed: (touchActive === 'progressBar' ? -1 : 1) * newScrollSpeed,
                        time: Date.now(),
                    };
                });
            }
        }
        return false;
    }, 14);
    var handleDragEnd = function () {
        setDragActive(false);
    };
    var handleTouchEnd = useCallback(function () {
        if (touchPosX || touchActive) {
            setTouchActive(null);
        }
        if (dragActive) {
            window.setTimeout(function () {
                handleDragEnd();
            }, 50);
        }
    }, [touchActive, dragActive, touchPosX]);
    var handleDragStart = function (event) {
        if (!touchMove)
            return;
        event.preventDefault();
        setDragActive(true);
    };
    var handleClick = function (event) {
        // Don't click on items if you just dragged the track
        if (dragActive)
            event.preventDefault();
    };
    var handleSliderHoverEnd = function () {
        if (pauseOnHover)
            setIsHovering(false);
    };
    var handleMouseLeave = function () {
        if (dragActive)
            handleTouchEnd();
        handleSliderHoverEnd();
    };
    var handleSliderHoverStart = function () {
        if (pauseOnHover)
            setIsHovering(true);
    };
    /** Children are mapped to SliderItemComponent. If LazyLoading === true: not yet visible items will be SkeletonComponent */
    var mappedChildren = useMemo(function () {
        var trackWrapperRects = getClientRects(trackWrapperRef.current);
        if (sliderWidth && trackWidth && trackWrapperRects) {
            var childrenArr = React.Children.toArray(children);
            if (infinite && frameSmallerThanTrack) {
                childrenArr = __spreadArrays(cycleSlice(childrenArr, -extraItemsForInfinite), childrenArr, cycleSlice(childrenArr, extraItemsForInfinite));
            }
            return childrenArr.map(function (child, index) { return (React.createElement(SliderItem, { index: index, variableWidth: variableWidth, slideGap: slideGap, lazyLoad: lazyLoad, lazyLoadItemWidth: lazyLoadItemWidth, lazyLoadThreshold: lazyLoadThreshold, trackWrapperBoundaryLeft: trackWrapperRects.left, trackWrapperBoundaryRight: trackWrapperRects.right, setLoadedSliderItems: setLoadedSliderItems, trackIsMoving: trackIsMoving, 
                // eslint-disable-next-line react/no-array-index-key
                key: index }, child)); });
        }
        return React.createElement(React.Fragment, null);
    }, [
        children,
        lazyLoadItemWidth,
        slideGap,
        sliderWidth,
        trackIsMoving,
        trackWidth,
        variableWidth,
        loadedSliderItems,
        extraItemsForInfinite,
    ]);
    return sliderItemsCount > 0 ? (React.createElement(SliderWrapper, { background: background, className: className, "data-testid": "SliderWrapper", ref: sliderWrapperRef, onMouseEnter: handleSliderHoverStart, onMouseLeave: handleMouseLeave, onMouseMove: handleTouchMove, onMouseUp: handleTouchEnd, onTouchCancel: handleTouchEnd, onTouchEnd: handleTouchEnd, onTouchMove: handleTouchMove },
        React.createElement(SliderInnerWrapper, { itemOverflow: itemOverflow, framePadding: framePadding },
            React.createElement(SliderCard, { headline: headline, hasCardLayout: hasCardLayout },
                React.createElement(TrackWrapper, { itemOverflow: itemOverflow, ref: trackWrapperRef, slideGap: slideGap, sliderInnerWidth: sliderInnerWidth },
                    React.createElement(StyledTrack, { "data-testid": "track", draggable: touchMove, maxScrollPosRight: maxScrollPosRight, onClick: handleClick, onTouchStart: handleTouchStart('track'), onMouseDown: handleTouchStart('track'), onDragStart: handleDragStart, ref: trackRef, scrollOvershootTime: scrollOvershootTime, scrollPosition: posX.value, scrollDistance: currentScrollDistance || scrollDistance, slideGap: slideGap, sliderPaddingBottom: sliderPaddingBottom, speed: speed, touchActive: touchActive, trackWidth: trackWidth, trackIsMoving: trackIsMoving, variableWidth: variableWidth }, mappedChildren),
                    React.createElement(CustomLink, { to: customLinkProps && customLinkProps.to, text: customLinkProps && customLinkProps.text }))),
            trackWidth > 1 ? (React.createElement(ProgressIndicator, { "data-testid": "progressIndicator", clickProgressDot: clickProgressDot, currentScrollDistance: currentScrollDistance, dots: dots, dotsColor: dotsColor, dotsColorInactive: dotsColorInactive, dotsDistance: dotsDistance, dotsSize: dotsSize, extraItemsForInfinite: extraItemsForInfinite, framePadding: framePadding, handleDragStart: handleDragStart, handleTouchStart: handleTouchStart, progress: progress, progressBar: progressBar, progressBarHeight: progressBarHeight, progressBarColor: progressBarColor, progressBarDistance: progressBarDistance, scrollDistance: scrollDistance, scrollOvershootTime: scrollOvershootTime, sliderInnerWidth: sliderInnerWidth, slideGap: slideGap, sliderPaddingBottom: sliderPaddingBottom, speed: speed, touchActive: touchActive, virtualTrackWidth: virtualTrackWidth })) : (React.createElement(React.Fragment, null))),
        arrows && (React.createElement(React.Fragment, null,
            React.createElement(ScrollControl, { sliderPaddingBottom: sliderPaddingBottom },
                React.createElement(ScrollButtonWrapper, { framePadding: framePadding, arrowsDistance: arrowsDistance, visible: (!arrowsOnHover || isHovering) &&
                        (progress > 0 || (Boolean(extraItemsForInfinite) && frameSmallerThanTrack)), onClick: function () { return scroll('left'); } }, prevArrow)),
            React.createElement(ScrollControlRight, { sliderPaddingBottom: sliderPaddingBottom },
                React.createElement(ScrollButtonWrapper, { right: true, framePadding: framePadding, arrowsDistance: arrowsDistance, visible: (!arrowsOnHover || isHovering) &&
                        ((trackWidth > sliderInnerWidth - slideGap && progress < 1) ||
                            (Boolean(extraItemsForInfinite) && frameSmallerThanTrack)), onClick: function () { return scroll('right'); } }, nextArrow)))))) : (React.createElement(React.Fragment, null));
};
/**
 * The Slider allows all slider settings of the basic Slider and a styleType attribute:
 * @param styleType it preconfigures some slider settings. Available from start: "default", "type1", "type2"
 * @param props slider settings that will overwrite/extend preconfigured settings of styleType. More information in the "./README.md"
 */
var Slider = function (_a) {
    var _b = _a.styleType, styleType = _b === void 0 ? 'default' : _b, props = __rest(_a, ["styleType"]);
    var _c = props, children = _c.children, responsive = _c.responsive;
    var styleTypeProps = getStyleTypeConfig(styleType);
    var extendedProps = __assign(__assign(__assign({}, styleTypeProps), props), { responsive: __spreadArrays((styleTypeProps.responsive || []), (responsive || [])) });
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    return React.createElement(BasicSlider, __assign({}, extendedProps), children);
};
export { Slider };
export default Slider;
//# sourceMappingURL=index.js.map